import { Divider, Stack, DialogContent, DialogActions, Dialog, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { get } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import dayjs from 'dayjs';

import { Strings } from '@app/constants';
import { useRequestState } from '@app/hooks';
import { Header } from '@app/sections/header';
import { FormProvider, RHFTextField, RHFUploadAvatar } from '@app/components';
import { IAddUserSchema, IUser } from '../types';
import { editVendorSchema } from '../validator';
import { addVendor } from '../slice';
import { selectAddVendorState } from '../selectors';
import { IMAGE_BASE_URL } from '@app/configs';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IUser;
}

/**
 * Nationwide vendor edit dialog.
 * Geo/coverage/slot fields are hidden — backend keeps existing address data.
 */
const AddVendor = ({ open, onClose, row }: Props) => {
  const dispatch = useDispatch();

  const methods = useForm<IAddUserSchema>({
    resolver: yupResolver(editVendorSchema) as any,
    defaultValues: {
      name: get(row, 'firstName', ''),
      country: get(row?.addresses?.[0], 'country', 'India'),
      // Preserved for API update (not shown in UI)
      description: get(row?.addresses?.[0], 'description', ''),
      address: get(row?.addresses?.[0], 'address', 'Nationwide'),
      mobileNumber: get(row?.addresses?.[0], 'mobileNumber', get(row, 'phone', '')),
      city: get(row?.addresses?.[0], 'city', 'India'),
      coverageArea: String(get(row?.addresses?.[0], 'coverageArea', 0) ?? 0),
      latitude: String(get(row?.addresses?.[0]?.location, 'lat', '20.5937') ?? '20.5937'),
      longitude: String(get(row?.addresses?.[0]?.location, 'long', '78.9629') ?? '78.9629'),
      vendorType: get(row, 'vendorType', 'Retialer'),
      email: get(row, 'email', ''),
      startTime: row?.startTime ? dayjs(`2022-01-01 ${row.startTime}`) : dayjs('2022-01-01 00:00'),
      endTime: row?.endTime ? dayjs(`2022-01-01 ${row.endTime}`) : dayjs('2022-01-01 23:59'),
      image: row?.profileImage ? IMAGE_BASE_URL + row?.profileImage : '',
      searchAddress: get(row?.addresses?.[0], 'address', 'Nationwide'),
      slotSkip: get(row, 'slotSkip', 0),
    },
  });

  useEffect(() => {
    methods.reset({
      name: get(row, 'firstName', ''),
      country: get(row?.addresses?.[0], 'country', 'India'),
      description: get(row?.addresses?.[0], 'description', ''),
      address: get(row?.addresses?.[0], 'address', 'Nationwide'),
      mobileNumber: get(row?.addresses?.[0], 'mobileNumber', get(row, 'phone', '')),
      city: get(row?.addresses?.[0], 'city', 'India'),
      coverageArea: String(get(row?.addresses?.[0], 'coverageArea', 0) ?? 0),
      latitude: String(get(row?.addresses?.[0]?.location, 'lat', '20.5937') ?? '20.5937'),
      longitude: String(get(row?.addresses?.[0]?.location, 'long', '78.9629') ?? '78.9629'),
      vendorType: get(row, 'vendorType', 'Retialer'),
      email: get(row, 'email', ''),
      startTime: row?.startTime ? dayjs(`2022-01-01 ${row.startTime}`) : dayjs('2022-01-01 00:00'),
      endTime: row?.endTime ? dayjs(`2022-01-01 ${row.endTime}`) : dayjs('2022-01-01 23:59'),
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      image: row?.profileImage ? IMAGE_BASE_URL + row?.profileImage : '',
      searchAddress: get(row?.addresses?.[0], 'address', 'Nationwide'),
      slotSkip: get(row, 'slotSkip', 0),
    });
  }, [row, methods]);

  const { loading } = useRequestState({
    stateSelector: selectAddVendorState,
    errorShown: true,
    successMessageShown: true,
    onSuccess: onClose,
  });

  const onSubmit = (form: IAddUserSchema) => {
    const id = row?.id ?? 0;
    dispatch(
      addVendor({
        ...form,
        id,
      })
    );
  };

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (file) {
        methods.setValue('image', newFile, { shouldValidate: true });
      }
    },
    [methods]
  );

  return (
    <FormProvider methods={methods}>
      <Box sx={{ flexGrow: 1 }}>
        <Dialog open={open} fullWidth>
          <Header variant="dialog" title={Strings.header.editVendor} onClose={onClose} />
          <Divider />
          <DialogContent>
            <Stack spacing={2} my={2}>
              <RHFTextField
                name="name"
                required
                label={Strings.field.name}
                fullWidth
                variant="outlined"
              />
              <RHFTextField
                name="country"
                required
                label={Strings.field.country}
                fullWidth
                variant="outlined"
              />
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box display="flex" marginTop={2} flexWrap="wrap">
                    {Array.isArray(methods.getValues('image')) &&
                      methods.getValues('image').map((file: any, index: number) => (
                        <img
                          key={index}
                          src={row ? file : file.preview}
                          alt={`Preview ${index}`}
                          style={{
                            width: 115,
                            height: 115,
                            margin: '5px',
                            borderRadius: '10px',
                          }}
                        />
                      ))}
                    <RHFUploadAvatar
                      multiple
                      name="image"
                      onDrop={handleDrop}
                      sx={{
                        borderRadius: '10px',
                        width: 115,
                        height: 115,
                        border: 'none',
                        m: '5px',
                        background: 'grey',
                        opacity: 0.72,
                      }}
                      placeholderSx={{
                        borderRadius: '10px',
                        width: '100%',
                        height: '100%',
                        border: '5px dashed rgba(0, 0, 0, 0.08)',
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              loading={loading}
              type="button"
              variant="contained"
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Box>
    </FormProvider>
  );
};

export default AddVendor;
